import _orderBy from 'lodash/orderBy';

import { getLayerBaseProperties } from './getLayerBaseProperties';
import { getLayerProperties } from './getLayerProperties';

const _orderLayers = (layers: any) =>
  _orderBy(
    layers.map((layer: any, layerIndex: any) => ({
      ...layer,
      layerOrder: layers.length - 1 - layerIndex,
    })),
    [
      (layer: any) => {
        return layer.layerOrder;
      },
    ],
    ['asc'],
  );

const _getDefaultLayerOptions = (layer: any) => {
  if (layer.type === 'TEXT') {
    return { copy: false };
  }

  return {};
};

const _formatNextLayer = ({ layer }: { layer: any }) => ({
  id: layer.id,
  name: layer.name,
  type: layer.type,
  children: layer.children,
  properties: [
    ...getLayerBaseProperties({ layer }),
    ...getLayerProperties({ layer }),
  ],
});

export const transformLayers = ({
  layers,
  currentLayers,
  parent,
}: {
  layers: any;
  currentLayers: any;
  parent: any;
}): any => {
  const nextLayers = [];

  const orderedLayers = _orderLayers(layers);

  for (let i = 0; i < orderedLayers.length; i++) {
    const nextLayer = _formatNextLayer({
      layer: {
        ...orderedLayers[i],
        parent,
      },
    });

    const currentLayer = currentLayers.find(
      (layer: any) => layer.id === nextLayer.id,
    );

    if (!currentLayer) {
      nextLayers.push({
        ...nextLayer,
        options: _getDefaultLayerOptions(nextLayer),
        properties: nextLayer.properties.map((nextProperty: any) => {
          return {
            ...nextProperty,
            check: false,
          };
        }),
        children:
          nextLayer.children && nextLayer.children.length
            ? transformLayers({
                layers: nextLayer.children,
                currentLayers: [],
                parent: {
                  type: nextLayer.type,
                  name: nextLayer.name,
                  // Children need it to tell whether the parent lays them out.
                  layoutMode: orderedLayers[i].layoutMode,
                  relativeTransform: orderedLayers[i].relativeTransform,
                  parent,
                },
              })
            : [],
        parent,
      });
    } else {
      nextLayers.push({
        ...nextLayer,
        options:
          currentLayer.options && Object.keys(currentLayer.options).length > 0
            ? currentLayer.options
            : _getDefaultLayerOptions(nextLayer),
        properties: nextLayer.properties.map((nextProperty: any) => {
          const currentProperty = currentLayer?.properties.find(
            (property: any) => property.key === nextProperty.key,
          );

          if (currentProperty) {
            if (currentProperty.key === 'reactions') {
              const nextReactions = Array.isArray(nextProperty.value)
                ? nextProperty.value
                : [];
              const currentReactions = Array.isArray(currentProperty.value)
                ? currentProperty.value
                : [];

              return {
                ...nextProperty,
                check: currentProperty.check,
                value: nextReactions.map(
                  (reaction: Record<string, unknown>, reactionIndex: number) => ({
                    ...reaction,
                    checks:
                      currentReactions[reactionIndex]?.checks &&
                      typeof currentReactions[reactionIndex].checks === 'object'
                        ? currentReactions[reactionIndex].checks
                        : {},
                  }),
                ),
              };
            }

            return {
              ...nextProperty,
              check: currentProperty.check,
              value:
                currentProperty.key === 'pointCount' ||
                currentProperty.key === 'innerRadius' ||
                currentProperty.key === 'vectorPaths' ||
                currentProperty.key === 'textOptions' ||
                currentProperty.key === 'listOptions' ||
                currentProperty.key === 'strokeCap' ||
                currentProperty.key === 'layoutPositioning' ||
                currentProperty.key === 'componentPropertyReferences' ||
                currentProperty.key === 'variantProperties' ||
                currentProperty.key === 'componentPropertyDefinitions' ||
                currentProperty.key === 'mainComponent' ||
                currentProperty.key === 'masterComponent' ||
                currentProperty.key === 'componentProperties' ||
                currentProperty.key === 'styles'
                  ? currentProperty.value
                  : nextProperty.value,
            };
          }

          return {
            ...nextProperty,
            check: false,
          };
        }),
        children:
          nextLayer.children && nextLayer.children.length
            ? transformLayers({
                layers: nextLayer.children,
                currentLayers: currentLayer.children || [],
                parent: {
                  type: nextLayer.type,
                  name: nextLayer.name,
                  // Children need it to tell whether the parent lays them out.
                  layoutMode: orderedLayers[i].layoutMode,
                  relativeTransform: orderedLayers[i].relativeTransform,
                  parent,
                },
              })
            : [],
        parent,
      });
    }
  }

  return nextLayers;
};
